import Box from "@mui/material/Box"
import { type FC, useRef, MouseEvent, useState, useEffect } from "react"
import Grid from "@mui/material/Grid"
import Button from "@mui/material/Button"
import { Step2Props } from "../types"
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
    Link,
    MenuItem,
    Radio,
    RadioGroup,
    Skeleton,
    Stack,
    TextField,
    Theme,
    Typography,
    useMediaQuery,
} from "@mui/material"
import { useGtmPageView } from "../../hooks"
import { twMerge } from "tailwind-merge"
import { AvatarPlayer } from "../../common"
import { ServicePackage } from "../components/ServicePackage"
import { useDynamicDataMutation, useDynamicImageQuery } from "../../../../api/dynamicSites/hooks"
import { ImageSwiper, KeyModal } from "../components"
import { Controller } from "react-hook-form"
import { StaticDatePicker } from "@mui/x-date-pickers"
import { enqueueSnackbar } from "notistack"
import { ProblemDetails } from "../../../../api/errors"
import { dateTimeToApiString } from "../../../../utils/dateTimeUtils"
import { useUpdateWithoutRerender } from "../../../../api/activationObject/hooks"

type KeysService = "remove keys" | "return keys" | undefined

const Step2View: FC<Step2Props> = (props) => {
    const {
        form,
        activationObject,
        dynamicSiteData,
        dynamicDataLoading,
        handleSubAction,
        handlePrimaryAction,
        dynamicImages,
        keyModalActions,
        conditions,
        options,
    } = props

    const { handleSubmit, watch, control, setValue } = form.form

    useGtmPageView({ pageName: "Step2", synonym: activationObject?.synonym })
    const { mutate: mutateDynamicData } = useDynamicDataMutation()
    const updateMutation = useUpdateWithoutRerender()

    const formRef = useRef<HTMLFormElement>(null)

    const handoverDate = watch("handoverDate")
    const backHandoverDate = watch("backHandoverDate")
    const handoverHour = watch("handoverHour")
    const backHandoverHour = watch("backHandoverHour")
    const destroyKeyValue = watch("destroyKey")

    const [disableDatePickers, setDisableDatePickers] = useState(false)
    const [activePackageImageName, setActivePackageImageName] = useState<string>()
    const { data: popupImage, isLoading: popupImageLoading } = useDynamicImageQuery(activePackageImageName)

    const [keysService, setKeysService] = useState<KeysService>()

    const handleKeysServiceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setKeysService((event.target as HTMLInputElement).value as KeysService)
        if ((event.target as HTMLInputElement).value === "remove keys") {
            setValue("backHandoverDate", undefined)
            setValue("backHandoverHour", undefined)
            setValue("destroyKey", true)
        } else {
            setValue("destroyKey", false)
        }
    }

    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.between("xs", "md"))
    const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.between("md", "lg"))

    const isPortable = isMobile || isTablet

    const [playMainAvatar, setPlayMainAvatar] = useState(true)

    useEffect(() => {
        setDisableDatePickers(
            (keysService !== "return keys" && keyModalActions.showBackKeyModal) || (keysService === undefined && keyModalActions.showBackKeyModal)
        )
    }, [keyModalActions.showBackKeyModal, keyModalActions.showTakeKeyModal, keysService])

    useEffect(() => {
        if (activationObject && (!!activationObject.handoverKeysDateTime || !!activationObject?.backHandoverKeysDateTime)) {
            setDisableDatePickers(false)
        }
    }, [activationObject])

    useEffect(() => {
        if (activationObject && activationObject.destroyKey) {
            setKeysService("remove keys")
        }
    }, [activationObject])

    return (
        <form ref={formRef} onSubmit={handleSubmit(form.onSubmit)} method="post" autoComplete="off" id="step2form">
            {keyModalActions.showTakeKeyModal ? (
                <KeyModal
                    title={"Kdy si máme přijet pro klíče od objektu?"}
                    handleClose={keyModalActions.handleTakeKeyModalClose}
                    open={keyModalActions.showTakeKeyModal}
                    videoName={"TAKE_KEY_VIDEO.mp4"}
                    isPortable={isPortable}
                    // className='last-of-type:!hidden [&:nth-of-type(2)]:!hidden'
                >
                    <Stack
                        direction={{
                            xs: "column",
                            lg: "row",
                        }}
                        justifyContent="stretch"
                        spacing={3}
                    >
                        <Stack className="gap-4">
                            <Controller
                                name={"handoverDate"}
                                control={control}
                                render={(params) => (
                                    <StaticDatePicker
                                        {...params}
                                        className="bg-pantone-white-90"
                                        disabled={disableDatePickers}
                                        slotProps={{
                                            toolbar: {
                                                className: "hidden",
                                            },
                                            actionBar: {
                                                className: "hidden",
                                            },
                                        }}
                                        disablePast
                                        value={params.field.value || ""}
                                        view="day"
                                        views={["day"]}
                                        minDate={conditions.handoverMinDate}
                                        onChange={(e) => {
                                            params.field.onChange(e)
                                            setValue("handoverHour", null)
                                            setValue("backHandoverHour", null)
                                        }}
                                        // inputFormat='dd.MM.yyyy'
                                        shouldDisableDate={conditions.isDayDisabled}
                                    />
                                )}
                            />
                            {popupImageLoading ? (
                                <Skeleton className="w-80 h-[200px]" animation="wave" />
                            ) : !!popupImage ? (
                                <img src={popupImage} alt="back key modal info" className="rounded w-80 h-[200px] object-cover" />
                            ) : null}
                        </Stack>
                        <Stack className="w-full max-h-[275px]">
                            <Stack>
                                <Controller
                                    name={"handoverHour"}
                                    disabled={disableDatePickers || !options.handoverTimes}
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            value={field.value || ""}
                                            fullWidth
                                            select
                                            label="Dostupné časy příjezdu"
                                            className="mb-6"
                                        >
                                            {options.handoverTimes &&
                                                options.handoverTimes.map((option, index) => (
                                                    <MenuItem key={index} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                        </TextField>
                                    )}
                                />
                                <FormControlLabel
                                    className="mb-4 lg:mb-0"
                                    control={
                                        <Checkbox
                                            checked={disableDatePickers}
                                            defaultValue={undefined}
                                            size="small"
                                            onChange={(e, checked) => {
                                                setDisableDatePickers((prevState) => !prevState)
                                                setValue("handoverDate", undefined)
                                                setValue("handoverHour", undefined)
                                            }}
                                        />
                                    }
                                    componentsProps={{
                                        typography: {
                                            className: "text-base",
                                        },
                                    }}
                                    label="Nechci předávat klíče od objektu"
                                />
                            </Stack>
                            <Stack direction="row" className="mt-auto gap-4">
                                <Button variant="outlined" size="large" color="secondary" onClick={keyModalActions.handleTakeKeyModalClose}>
                                    zrušit
                                </Button>
                                <Button
                                    variant="contained"
                                    disabled={!disableDatePickers && (!!!handoverDate || !!!handoverHour)}
                                    size="large"
                                    color="primary"
                                    className="shadow-none"
                                    // type='submit'
                                    // form='step2form'
                                    onClick={() => {
                                        if (activationObject) {
                                            const { actualWizardStep, previousWizardStep, ...rest } = activationObject

                                            const handoverKeysDateTime = handoverDate
                                            const backHandoverKeysDateTime = backHandoverDate

                                            if (handoverKeysDateTime) {
                                                handoverKeysDateTime.setHours(handoverHour || 0)
                                                handoverKeysDateTime.setMinutes(0)
                                            }

                                            if (backHandoverKeysDateTime) {
                                                backHandoverKeysDateTime.setHours(backHandoverHour || 0)
                                                backHandoverKeysDateTime.setMinutes(0)
                                            }
                                            updateMutation.mutate(
                                                {
                                                    ...rest,
                                                    handoverKeysDateTime: handoverKeysDateTime ? dateTimeToApiString(handoverKeysDateTime) : null,
                                                    backHandoverKeysDateTime: backHandoverKeysDateTime
                                                        ? dateTimeToApiString(backHandoverKeysDateTime)
                                                        : null,
                                                },
                                                {
                                                    onSuccess: () => {
                                                        keyModalActions.handleTakeKeyModalClose()
                                                        keyModalActions.handleInvoiceRedirect()
                                                    },
                                                    onError: (error: ProblemDetails) => {
                                                        enqueueSnackbar(`Něco se pokazilo, zkuste to prosím znovu: ${error.detail}`, {
                                                            variant: "error",
                                                        })
                                                    },
                                                }
                                            )
                                        }
                                    }}
                                >
                                    Potvrdit
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                </KeyModal>
            ) : null}
            {keyModalActions.showBackKeyModal ? (
                <KeyModal
                    title={"Kdy Vám máme vrátit klíče od objektu?"}
                    handleClose={keyModalActions.handleBackKeyModalClose}
                    open={keyModalActions.showBackKeyModal}
                    videoName={"BACK_KEY_VIDEO.mp4"}
                    isPortable={isPortable}
                >
                    <Stack
                        direction={{
                            xs: "column",
                            xl: "row",
                        }}
                        justifyContent="stretch"
                        spacing={3}
                    >
                        <Stack className="gap-4">
                            <Controller
                                name={"backHandoverDate"}
                                control={control}
                                render={(params) => (
                                    <StaticDatePicker
                                        {...params}
                                        className="bg-pantone-white-90"
                                        disabled={disableDatePickers}
                                        slotProps={{
                                            toolbar: {
                                                className: "hidden",
                                            },
                                            actionBar: {
                                                className: "hidden",
                                            },
                                        }}
                                        disablePast
                                        value={params.field.value || ""}
                                        view="day"
                                        views={["day"]}
                                        minDate={conditions.handoverMinDate}
                                        onChange={(e) => {
                                            params.field.onChange(e)
                                            setValue("handoverHour", null)
                                            setValue("backHandoverHour", null)
                                        }}
                                        // inputFormat='dd.MM.yyyy'
                                        shouldDisableDate={conditions.isDayDisabled}
                                    />
                                )}
                            />
                            {popupImageLoading ? (
                                <Skeleton className="w-80 h-[200px]" animation="wave" />
                            ) : !!popupImage ? (
                                <img src={popupImage} alt="back key modal info" className="rounded w-80 h-[200px] object-cover" />
                            ) : null}
                        </Stack>
                        <Stack className="w-full max-h-[275px]">
                            <Stack>
                                {options.handoverTimes && (
                                    <Controller
                                        name={"backHandoverHour"}
                                        disabled={disableDatePickers || !options.handoverTimes}
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                value={field.value || ""}
                                                fullWidth
                                                select
                                                label="Dostupné časy příjezdu"
                                                className="mb-6"
                                            >
                                                {options.handoverTimes!.map((option, index) => (
                                                    <MenuItem key={index} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />
                                )}
                                <FormControl>
                                    <FormLabel>Typ zpracování klíčů</FormLabel>
                                    <RadioGroup defaultValue={undefined} value={keysService} onChange={handleKeysServiceChange}>
                                        <FormControlLabel
                                            value="remove keys"
                                            control={<Radio size="small" color="primary" />}
                                            componentsProps={{
                                                typography: {
                                                    fontSize: "0.875rem",
                                                },
                                            }}
                                            label="Chci skartovat klíče"
                                        />
                                        <FormControlLabel
                                            value="return keys"
                                            control={<Radio size="small" color="primary" />}
                                            componentsProps={{
                                                typography: {
                                                    fontSize: "0.875rem",
                                                },
                                            }}
                                            label="Chci vrátit klíče"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Stack>
                            <Stack direction="row" className="mt-auto gap-4">
                                <Button variant="outlined" size="large" color="secondary" onClick={keyModalActions.handleBackKeyModalClose}>
                                    zrušit
                                </Button>
                                <Button
                                    variant="contained"
                                    disabled={(!disableDatePickers || !keysService) && (!!!backHandoverDate || !!!backHandoverHour)}
                                    size="large"
                                    color="primary"
                                    className="shadow-none"
                                    // type='submit'
                                    // form='step2form'
                                    onClick={() => {
                                        if (activationObject) {
                                            const { actualWizardStep, previousWizardStep, ...rest } = activationObject

                                            const handoverKeysDateTime = handoverDate
                                            const backHandoverKeysDateTime = backHandoverDate

                                            if (handoverKeysDateTime) {
                                                handoverKeysDateTime.setHours(handoverHour || 0)
                                                handoverKeysDateTime.setMinutes(0)
                                            }

                                            if (backHandoverKeysDateTime) {
                                                backHandoverKeysDateTime.setHours(backHandoverHour || 0)
                                                backHandoverKeysDateTime.setMinutes(0)
                                            }
                                            updateMutation.mutate(
                                                {
                                                    ...rest,
                                                    handoverKeysDateTime: handoverKeysDateTime ? dateTimeToApiString(handoverKeysDateTime) : null,
                                                    destroyKey: !!!destroyKeyValue ? false : true,
                                                    backHandoverKeysDateTime: backHandoverKeysDateTime
                                                        ? dateTimeToApiString(backHandoverKeysDateTime)
                                                        : null,
                                                },
                                                {
                                                    onSuccess: () => {
                                                        keyModalActions.handleBackKeyModalClose()
                                                        keyModalActions.handleInvoiceRedirect()
                                                    },
                                                    onError: (error: ProblemDetails) => {
                                                        enqueueSnackbar(`Něco se pokazilo, zkuste to prosím znovu: ${error.detail}`, {
                                                            variant: "error",
                                                        })
                                                    },
                                                }
                                            )
                                        }
                                    }}
                                >
                                    Potvrdit
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                </KeyModal>
            ) : null}
            {!dynamicDataLoading && dynamicSiteData?.type !== "Selection" ? (
                <Grid container p={2}>
                    <Grid item xs={12} lg={7} pb={{ xs: 0, lg: 5 }}>
                        <Box className="pb-14">
                            {dynamicDataLoading ? (
                                <>
                                    <Skeleton
                                        variant="text"
                                        className="text-2xl pb-4 md:text-4xl md:pb-8"
                                        sx={{
                                            fontSize: "2rem",
                                        }}
                                    />
                                    <Skeleton variant="text" className="text-base" />
                                </>
                            ) : (
                                <>
                                    <Typography className="font-bold text-2xl pb-4 md:text-4xl md:pb-8" variant="h1">
                                        {dynamicSiteData?.title}
                                    </Typography>
                                    <Typography variant="body1" paragraph className="font-thin mb-8 lg:mb-0">
                                        {dynamicSiteData?.subtitle}
                                    </Typography>
                                    {dynamicDataLoading ? (
                                        "Načítám avatara..."
                                    ) : isPortable ? (
                                        <AvatarPlayer className="lg:hidden" videoName={dynamicSiteData?.video?.[0]} />
                                    ) : null}
                                </>
                            )}
                        </Box>
                        <Stack
                            direction="row"
                            spacing={2}
                            className='py-3 pr-3 mb-6 pl-20 md:pl-0 lg:mb-0 lg:pr-0 lg:pt-0 lg:pb-14 items-start bg-pantone-gray-1 lg:bg-transparent -ml-8 lg:ml-0 rounded-r-xl lg:rounded-none w-[115%] sm:w-[105%] md:w-auto relative before:content-[""] before:w-[20%] lg:before:hidden before:h-px before:bg-pantone-white-90 before:absolute before:left-0 before:bottom-4'
                            justifyContent={{ xs: "right", lg: "left" }}
                            flexWrap={{ xs: "wrap", lg: "nowrap" }}
                            rowGap={1}
                        >
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="large"
                                disabled={dynamicDataLoading}
                                // type='submit'
                                onClick={() => handleSubAction()}
                                className={twMerge(
                                    "normal-case py-[0.49rem] md:py-3 md:pl-6 font-semibold md:font-normal rounded-md text-pantone-gray-2 text-[0.98rem] md:text-2xl leading-[1.4375rem] shadow-none bg-pantone-white-90 lg:bg-transparent",
                                    !dynamicDataLoading && !dynamicSiteData?.subAction && "hidden"
                                )}
                            >
                                {dynamicDataLoading ? "Načítání..." : dynamicSiteData?.subAction?.title}
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                disabled={dynamicDataLoading}
                                // type='submit'
                                onClick={() => !!handlePrimaryAction && handlePrimaryAction()}
                                className={twMerge(
                                    "normal-case py-[0.49rem] md:py-3 md:pl-6 font-semibold md:font-normal rounded-md text-pantone-gray-2 text-[0.98rem] md:text-2xl leading-[1.4375rem] shadow-none",
                                    !dynamicDataLoading && !dynamicSiteData?.primaryAction && "hidden"
                                )}
                            >
                                {dynamicDataLoading ? "Načítání..." : dynamicSiteData?.primaryAction?.title}
                            </Button>
                        </Stack>
                        {!!dynamicImages &&
                        !dynamicDataLoading &&
                        !!dynamicSiteData?.images?.[0].seconds &&
                        !dynamicImages?.imagesLoading &&
                        !!dynamicImages ? (
                            <ImageSwiper dynamicDataLoading={dynamicDataLoading} dynamicImages={dynamicImages} dynamicSiteData={dynamicSiteData} />
                        ) : null}
                        {!dynamicDataLoading && !!dynamicSiteData?.leavingTable ? (
                            <Stack className="border border-solid border-pantone-yellow rounded-md overflow-hidden w-fit">
                                <Typography className="pl-[2.125rem] font-semibold text-2xl leading-none pt-8">Aktuálně zasahujeme</Typography>
                                <Stack className="gap-x-24 pl-[2.125rem] pb-[1.437rem] justify-stretch flex-wrap-reverse" direction="row">
                                    <Stack className="gap-2 pt-[1.437rem]">
                                        {dynamicSiteData?.leavingTable?.dateTimeLeavings?.map((leavingItem) => (
                                            <Stack
                                                direction="row"
                                                className='text-base relative gap-[6.438rem] last:before:hidden before:content-[""] before:w-[120%] before:h-px before:bg-pantone-gray-1 before:absolute before:-left-10 before:-bottom-[0.26rem]'
                                            >
                                                <Typography fontSize={"inherit"}>{leavingItem.date}</Typography>
                                                <Typography fontSize={"inherit"}>{leavingItem.time}</Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        className="border-solid border-0 border-t border-pantone-yellow pt-6 lg:pb-0"
                                    >
                                        <Stack className="pr-[2.125rem] md:pb-[1.437rem]">
                                            <Typography className="text-6xl font-semibold">
                                                {dynamicSiteData?.leavingTable.count.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                                            </Typography>
                                            <Typography className="text-sm leading-none font-semibold">výjezdů</Typography>
                                            <Typography className="text-sm leading-none font-light">/posledních 30 dní</Typography>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </Stack>
                        ) : null}
                    </Grid>
                    <Grid item xs={0} lg={1} className="hidden lg:block"></Grid>
                    <Grid item lg={4} className="hidden lg:block">
                        {dynamicDataLoading ? "Načítám avatara..." : !isPortable ? <AvatarPlayer videoName={dynamicSiteData?.video?.[0]} /> : null}
                    </Grid>
                </Grid>
            ) : (
                <Box className="text-center mt-5 lg:mt-0 mb-9">
                    <Box className="pb-14 max-w-[740px] mx-auto">
                        {dynamicDataLoading ? (
                            <>
                                <Skeleton
                                    variant="text"
                                    className="text-2xl pb-4 md:text-4xl md:pb-8"
                                    sx={{
                                        fontSize: "2rem",
                                    }}
                                />
                                <Skeleton variant="text" className="text-base" />
                            </>
                        ) : (
                            <>
                                <Typography className="font-bold text-2xl pb-4 md:text-4xl md:pb-8" variant="h1">
                                    {dynamicSiteData?.title}
                                </Typography>
                                <Typography variant="body1" paragraph className="font-thin">
                                    {dynamicSiteData?.subtitle}
                                </Typography>
                                {dynamicDataLoading ? (
                                    "Načítám avatara..."
                                ) : isPortable ? (
                                    <AvatarPlayer className="lg:hidden" videoName={dynamicSiteData?.video?.[0]} shouldPlay={playMainAvatar} />
                                ) : null}
                            </>
                        )}
                    </Box>
                    <Grid
                        container
                        justifyContent={Number(dynamicSiteData?.dynamicSitePackages?.length) <= 2 ? "space-between" : "center"}
                        className="pb-14"
                    >
                        <Grid item xs={12} lg={Number(dynamicSiteData?.dynamicSitePackages?.length) <= 2 ? 7.4 : 12}>
                            <Stack
                                direction={{ xs: "column", md: "row" }}
                                alignItems={{ xs: "center", md: "flex-start" }}
                                justifyContent={{
                                    xs: "center",
                                    lg: Number(dynamicSiteData?.dynamicSitePackages?.length) <= 2 ? "flex-end" : "center",
                                }}
                                className="flex-wrap gap-6 md:gap-9"
                            >
                                {!dynamicDataLoading &&
                                    dynamicSiteData?.dynamicSitePackages?.map((item, key) => (
                                        <ServicePackage
                                            item={item}
                                            key={key}
                                            handleButtonClick={() => {
                                                setPlayMainAvatar(false)
                                                item.popupInfoImages && setActivePackageImageName(item.popupInfoImages?.[0])
                                            }}
                                            {...props}
                                        />
                                    ))}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} lg={4.6} className="hidden lg:block">
                            <Stack
                                direction="row"
                                justifyContent={{
                                    xs: "center",
                                    lg: "flex-end",
                                }}
                            >
                                {Number(dynamicSiteData?.dynamicSitePackages?.length) <= 2 && (
                                    <AvatarPlayer className="-mt-16" videoName={dynamicSiteData?.video?.[0]} shouldPlay={playMainAvatar} />
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                    <Stack className="mx-auto gap-2">
                        {!dynamicDataLoading && (
                            <>
                                {dynamicSiteData?.showAllPackagesLink && (
                                    <Link
                                        component="button"
                                        color="inherit"
                                        type="button"
                                        className={twMerge("text-pantone-gray-2 underline")}
                                        onClick={(e: MouseEvent) => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            mutateDynamicData({
                                                synonym: activationObject?.synonym!,
                                                mainSite: "Packages",
                                                type: "Redirect",
                                                dynamicSiteNumber: dynamicSiteData.dynamicSiteAllPackageId!,
                                            })
                                        }}
                                    >
                                        Zobrazit všechny služby
                                    </Link>
                                )}
                                {dynamicSiteData?.showRecomendedPackagesLink && (
                                    <Link
                                        component="button"
                                        color="inherit"
                                        type="button"
                                        className={twMerge("text-pantone-gray-2 underline")}
                                        onClick={(e: MouseEvent) => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            mutateDynamicData({
                                                synonym: activationObject?.synonym!,
                                                mainSite: "Packages",
                                                type: "Redirect",
                                                dynamicSiteNumber: dynamicSiteData.dynamicSiteRecomendedPackageId!,
                                            })
                                        }}
                                    >
                                        Zobrazit doporučené služby
                                    </Link>
                                )}
                                {dynamicSiteData?.showContactMePackagesLink && (
                                    <Link
                                        component="button"
                                        color="inherit"
                                        type="button"
                                        className={twMerge("text-pantone-gray-2 underline")}
                                        onClick={(e: MouseEvent) => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            mutateDynamicData({
                                                synonym: activationObject?.synonym!,
                                                mainSite: "Packages",
                                                type: "Redirect",
                                                dynamicSiteNumber: dynamicSiteData.dynamicSiteContactMePackageId!,
                                            })
                                        }}
                                    >
                                        Kontaktujte mě telefonicky
                                    </Link>
                                )}
                            </>
                        )}
                    </Stack>
                </Box>
            )}
        </form>
    )
}

export default Step2View
